import { useNavigate } from 'react-router-dom';

/**
 * A React hook that wraps window.fetch to automatically handle
 * HTTP 401 (Unauthorized) on /api/* routes by redirecting to /login.
 *
 * This ensures that whenever a session expires while the user is
 * actively working, they get redirected to the login page instead
 * of seeing raw error messages.
 */
export function setupAuthFetch(navigate: ReturnType<typeof useNavigate>, checkAuth: () => Promise<void>): void {
  const originalFetch = window.fetch;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : (input as URL).toString();
    const response = await originalFetch(input, init);

    // Only intercept API routes
    if (url.includes('/api/') && response.status === 401) {
      // Double-check the session before redirecting
      try {
        const sessionValid = await isSessionValid(originalFetch);
        if (!sessionValid) {
          // Clear auth state so that ProtectedRoute will redirect
          await checkAuth();
          navigate('/login', { replace: true });
          return response;
        }
      } catch {
        // If we can't verify, play it safe and redirect
        await checkAuth();
        navigate('/login', { replace: true });
        return response;
      }
    }

    return response;
  };
}

async function isSessionValid(originalFetch: typeof window.fetch): Promise<boolean> {
  try {
    const response = await originalFetch('/api/v1/auth/session');
    if (!response.ok) return false;
    const data = (await response.json()) as { valid?: boolean };
    return data.valid === true;
  } catch {
    return false;
  }
}
